import dagre from '@dagrejs/dagre';
import { Edge, Node, Position } from 'reactflow';

export const CUBE_NODE_LAYOUT = {
  width: 280,
  headerHeight: 58,
  memberSectionHeight: 24,
  dividerHeight: 2,
  sectionLabelHeight: 22,
  attributeRowHeight: 24,
  sectionSeparatorHeight: 6,
  emptyStateHeight: 30,
  bottomPad: 8,
  handleSize: 8,
} as const;

export const layoutOptions = {
  direction: 'RIGHT',
} as const;

export type Orientation = 'RIGHT' | 'DOWN';

const DAGRE_NODE_GAP = 140;
const DAGRE_RANK_GAP = 240;
const DAGRE_EDGE_GAP = 56;
const COMPONENT_GAP = 220;

interface CubeAttribute {
  handleId: string;
  role: 'INBOUND' | 'OUTBOUND';
}

interface CubeNodeData {
  cubeCode?: string;
  memberName?: string | null;
  attributes?: CubeAttribute[];
}

type NodePosition = { x: number; y: number };
type NodeBox = { x: number; y: number; width: number; height: number };
type NodeBoxMap = Map<string, NodeBox>;
type AdjacencyMap = Map<string, Set<string>>;

interface DagreLayoutOptions {
  direction?: Orientation;
}

function getOrderedAttributes(data?: CubeNodeData) {
  const attributes = data?.attributes || [];

  return {
    inbound: attributes.filter((attribute) => attribute.role === 'INBOUND'),
    outbound: attributes.filter((attribute) => attribute.role === 'OUTBOUND'),
  };
}

export function estimateCubeNodeHeight(data?: CubeNodeData): number {
  const { inbound, outbound } = getOrderedAttributes(data);

  let height = CUBE_NODE_LAYOUT.headerHeight;

  if (data?.memberName) {
    height += CUBE_NODE_LAYOUT.memberSectionHeight;
  }

  height += CUBE_NODE_LAYOUT.dividerHeight;

  if (inbound.length === 0 && outbound.length === 0) {
    return height + CUBE_NODE_LAYOUT.emptyStateHeight + CUBE_NODE_LAYOUT.bottomPad;
  }

  if (inbound.length > 0) {
    height += CUBE_NODE_LAYOUT.sectionLabelHeight + inbound.length * CUBE_NODE_LAYOUT.attributeRowHeight;
  }
  if (inbound.length > 0 && outbound.length > 0) {
    height += CUBE_NODE_LAYOUT.sectionSeparatorHeight;
  }
  if (outbound.length > 0) {
    height += CUBE_NODE_LAYOUT.sectionLabelHeight + outbound.length * CUBE_NODE_LAYOUT.attributeRowHeight;
  }

  return height + CUBE_NODE_LAYOUT.bottomPad;
}

function getNodeSize(node: Node<CubeNodeData>) {
  return {
    width: CUBE_NODE_LAYOUT.width,
    height: estimateCubeNodeHeight(node.data),
  };
}

function getSeedSortValue(node: Node<CubeNodeData>, orientation: Orientation) {
  const primary = orientation === 'RIGHT' ? node.position?.y || 0 : node.position?.x || 0;
  const secondary = orientation === 'RIGHT' ? node.position?.x || 0 : node.position?.y || 0;

  return { primary, secondary };
}

function sortNodesBySeed(nodes: Node<CubeNodeData>[], orientation: Orientation) {
  return [...nodes].sort((left, right) => {
    const leftSeed = getSeedSortValue(left, orientation);
    const rightSeed = getSeedSortValue(right, orientation);

    if (leftSeed.primary !== rightSeed.primary) {
      return leftSeed.primary - rightSeed.primary;
    }

    if (leftSeed.secondary !== rightSeed.secondary) {
      return leftSeed.secondary - rightSeed.secondary;
    }

    const leftCode = left.data?.cubeCode || left.id;
    const rightCode = right.data?.cubeCode || right.id;
    return leftCode.localeCompare(rightCode);
  });
}

function getUniqueEdges(edges: Edge[]) {
  const seen = new Set<string>();

  return edges.filter((edge) => {
    const key = `${edge.source}->${edge.target}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function buildAdjacencyMaps(nodeIds: Set<string>, edges: Edge[]) {
  const undirected: AdjacencyMap = new Map();

  nodeIds.forEach((nodeId) => {
    undirected.set(nodeId, new Set());
  });

  getUniqueEdges(edges).forEach((edge) => {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      return;
    }

    undirected.get(edge.source)?.add(edge.target);
    undirected.get(edge.target)?.add(edge.source);
  });

  return { undirected };
}

function getConnectedComponents(
  nodes: Node<CubeNodeData>[],
  edges: Edge[],
  orientation: Orientation
) {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const { undirected } = buildAdjacencyMaps(nodeIds, edges);
  const visited = new Set<string>();
  const components: string[][] = [];

  sortNodesBySeed(nodes, orientation).forEach((startNode) => {
    if (visited.has(startNode.id)) {
      return;
    }

    const queue = [startNode.id];
    const component: string[] = [];
    visited.add(startNode.id);

    while (queue.length > 0) {
      const currentNodeId = queue.shift();

      if (!currentNodeId) {
        continue;
      }

      component.push(currentNodeId);

      [...(undirected.get(currentNodeId) || [])]
        .sort((leftId, rightId) => {
          const leftNode = nodeMap.get(leftId);
          const rightNode = nodeMap.get(rightId);

          if (!leftNode || !rightNode) {
            return 0;
          }

          return sortNodesBySeed([leftNode, rightNode], orientation)[0].id === leftId ? -1 : 1;
        })
        .forEach((neighborNodeId) => {
          if (visited.has(neighborNodeId)) {
            return;
          }

          visited.add(neighborNodeId);
          queue.push(neighborNodeId);
        });
    }

    components.push(component);
  });

  return components.sort((leftComponent, rightComponent) => {
    const leftNode = nodeMap.get(leftComponent[0]);
    const rightNode = nodeMap.get(rightComponent[0]);

    if (!leftNode || !rightNode) {
      return 0;
    }

    return sortNodesBySeed([leftNode, rightNode], orientation)[0].id === leftNode.id ? -1 : 1;
  });
}

function getPairWeights(edges: Edge[]) {
  const weights = new Map<string, { source: string; target: string; weight: number }>();

  edges.forEach((edge) => {
    const key = `${edge.source}->${edge.target}`;
    const current = weights.get(key);

    if (current) {
      current.weight += 1;
      return;
    }

    weights.set(key, {
      source: edge.source,
      target: edge.target,
      weight: 1,
    });
  });

  return [...weights.values()];
}

function layoutComponentWithDagre(
  componentNodes: Node<CubeNodeData>[],
  componentEdges: Edge[],
  orientation: Orientation
) {
  const graph = new dagre.graphlib.Graph({ multigraph: true });
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({
    rankdir: orientation === 'RIGHT' ? 'LR' : 'TB',
    ranker: 'network-simplex',
    nodesep: DAGRE_NODE_GAP,
    ranksep: DAGRE_RANK_GAP,
    edgesep: DAGRE_EDGE_GAP,
    marginx: 24,
    marginy: 24,
  });

  sortNodesBySeed(componentNodes, orientation).forEach((node) => {
    const { width, height } = getNodeSize(node);
    graph.setNode(node.id, { width, height });
  });

  getPairWeights(componentEdges).forEach((pair) => {
    graph.setEdge(
      pair.source,
      pair.target,
      { weight: Math.max(1, pair.weight * 2) },
      `${pair.source}-${pair.target}`
    );
  });

  dagre.layout(graph);

  const boxByNodeId: NodeBoxMap = new Map();

  componentNodes.forEach((node) => {
    const dagreNode = graph.node(node.id);

    if (!dagreNode) {
      return;
    }

    const { width, height } = getNodeSize(node);

    boxByNodeId.set(node.id, {
      x: dagreNode.x - width / 2,
      y: dagreNode.y - height / 2,
      width,
      height,
    });
  });

  const width = Math.max(...[...boxByNodeId.values()].map((box) => box.x + box.width), CUBE_NODE_LAYOUT.width) + 24;
  const height =
    Math.max(...[...boxByNodeId.values()].map((box) => box.y + box.height), CUBE_NODE_LAYOUT.headerHeight) + 24;

  return { boxByNodeId, width, height };
}

function applyComponentOffsets(
  componentLayouts: Array<{ nodeIds: string[]; boxByNodeId: NodeBoxMap; width: number; height: number }>,
  orientation: Orientation
) {
  const positionByNodeId = new Map<string, NodePosition>();
  let xOffset = 0;
  let yOffset = 0;

  componentLayouts.forEach((componentLayout) => {
    componentLayout.nodeIds.forEach((nodeId) => {
      const box = componentLayout.boxByNodeId.get(nodeId);

      if (!box) {
        return;
      }

      positionByNodeId.set(nodeId, {
        x: box.x + xOffset,
        y: box.y + yOffset,
      });
    });

    if (orientation === 'RIGHT') {
      yOffset += componentLayout.height + COMPONENT_GAP;
    } else {
      xOffset += componentLayout.width + COMPONENT_GAP;
    }
  });

  return positionByNodeId;
}

export const getLayoutedElements = async (
  nodes: Node<CubeNodeData>[],
  edges: Edge[],
  options: DagreLayoutOptions = {}
) => {
  const orientation: Orientation = options.direction === 'DOWN' ? 'DOWN' : 'RIGHT';
  const components = getConnectedComponents(nodes, edges, orientation);
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const componentLayouts: Array<{ nodeIds: string[]; boxByNodeId: NodeBoxMap; width: number; height: number }> = [];

  components.forEach((componentNodeIds) => {
    const componentNodeSet = new Set(componentNodeIds);
    const componentNodes = componentNodeIds
      .map((nodeId) => nodeById.get(nodeId))
      .filter((node): node is Node<CubeNodeData> => Boolean(node));
    const componentEdges = edges.filter(
      (edge) => componentNodeSet.has(edge.source) && componentNodeSet.has(edge.target)
    );
    const componentLayout = layoutComponentWithDagre(componentNodes, componentEdges, orientation);

    componentLayouts.push({
      nodeIds: componentNodeIds,
      boxByNodeId: componentLayout.boxByNodeId,
      width: componentLayout.width,
      height: componentLayout.height,
    });
  });

  const positionByNodeId = applyComponentOffsets(componentLayouts, orientation);

  return {
    nodes: nodes.map((node) => ({
      ...node,
      sourcePosition: orientation === 'RIGHT' ? Position.Right : Position.Bottom,
      targetPosition: orientation === 'RIGHT' ? Position.Left : Position.Top,
      position: positionByNodeId.get(node.id) || node.position,
    })),
    edges,
  };
};
